import { ContactType } from "@/interfaces/contactType";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { ToastInterface, useToast } from "vue-toastification";

interface Props {
  immediate?: boolean;
}

interface CreateParams {
  name: string;
}

interface UpdateParams {
  id: number;
  name: string;
}

interface DeleteParams {
  id: number;
}

const getContactTypes = async (): Promise<ContactType[]> => {
  const { data } = await axios.get<ContactType[]>(
    "/api/internal/contact-types",
  );

  return data;
};

const createContactType = async (
  contactType: CreateParams,
  toast: ToastInterface,
) => {
  try {
    const { data } = await axios.post<ContactType>(
      "/api/internal/contact-types",
      contactType,
    );
    toast.success("Se creo correctamente el tipo de contacto");

    return data;
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const updateContactType = async (
  contactType: UpdateParams,
  toast: ToastInterface,
) => {
  try {
    const { data } = await axios.put<ContactType>(
      `/api/internal/contact-types/${contactType.id}`,
      contactType,
    );
    toast.success("Se actualizo correctamente el tipo de contacto");

    return data;
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const deleteContactType = async (
  params: DeleteParams,
  toast: ToastInterface,
) => {
  try {
    await axios.delete(`/api/internal/contact-types/${params.id}`);
    toast.success("Se elimino correctamente el tipo de contacto");
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const useContactTypes = ({ immediate = true }: Props = {}) => {
  const toast = useToast();

  const { data, isLoading } = useQuery(["contact-types"], getContactTypes, {
    enabled: immediate,
  });

  const createContactTypeMutation = useMutation((contactType: CreateParams) =>
    createContactType(contactType, toast),
  );

  const updateContactTypeMutation = useMutation((contactType: UpdateParams) =>
    updateContactType(contactType, toast),
  );

  const deleteContactTypeMutation = useMutation((params: DeleteParams) =>
    deleteContactType(params, toast),
  );

  return {
    contactTypes: data,
    isLoading,

    createContactTypeMutation: createContactTypeMutation.mutate,
    createContactTypeAsync: createContactTypeMutation.mutateAsync,
    createContactTypeLoading: createContactTypeMutation.isLoading,

    updateContactTypeMutation: updateContactTypeMutation.mutate,
    updateContactTypeAsync: updateContactTypeMutation.mutateAsync,
    updateContactTypeLoading: updateContactTypeMutation.isLoading,

    deleteContactTypeMutation: deleteContactTypeMutation.mutate,
    deleteContactTypeAsync: deleteContactTypeMutation.mutateAsync,
    deleteContactTypeLoading: deleteContactTypeMutation.isLoading,
  };
};

export default useContactTypes;
